import React, { createContext, useContext, useState, useCallback } from "react";
import type { EventSpeaker } from "@/types/events";

interface EventSpeakerDialogContextType {
  isOpen: boolean;
  mode: "create" | "edit";
  editingSpeaker: EventSpeaker | null;
  openCreateDialog: () => void;
  openEditDialog: (speaker: EventSpeaker) => void;
  closeDialog: () => void;
}

const EventSpeakerDialogContext = createContext<EventSpeakerDialogContextType | undefined>(undefined);

export const EventSpeakerDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [editingSpeaker, setEditingSpeaker] = useState<EventSpeaker | null>(null);

  const openCreateDialog = useCallback(() => {
    setMode("create");
    setEditingSpeaker(null);
    setIsOpen(true);
  }, []);

  const openEditDialog = useCallback((speaker: EventSpeaker) => {
    setMode("edit");
    setEditingSpeaker(speaker);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setEditingSpeaker(null);
  }, []);

  return (
    <EventSpeakerDialogContext.Provider
      value={{
        isOpen,
        mode,
        editingSpeaker,
        openCreateDialog,
        openEditDialog,
        closeDialog,
      }}
    >
      {children}
    </EventSpeakerDialogContext.Provider>
  );
};

export const useEventSpeakerDialog = () => {
  const context = useContext(EventSpeakerDialogContext);
  if (!context) {
    throw new Error("useEventSpeakerDialog must be used within EventSpeakerDialogProvider");
  }
  return context;
};
